import React, { FC, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Replay as ReplayIcon } from '@material-ui/icons';
import {
  DATA_EXPORT_BATCH_DELIVERIES,
  DataExportBatchDeliveriesData,
  DataExportBatchDeliveriesVars,
  DataExportDelivery,
  DataExportDeliveryChannel,
  REDELIVER_DELIVERY,
  RedeliverDeliveryData,
  RedeliverDeliveryVars,
} from '@/graph/data-export-admin';
import {
  DeliveryStatusChip,
  formatChannelSnapshot,
  redeliverOutcome,
  useTimestampFormatter,
  useInvoker,
  useSharedStyles,
} from './shared';

/**
 * Expansion content for a batch row in the history table. Shows every
 * `~~export_delivery` row attached to this batch (initial + every
 * redelivery attempt, oldest-first) with a per-row retry button.
 * Deliveries attach to the BATCH under the merged-batch model — the
 * delivered object is the batch's merged artifact.
 *
 * Fired lazily by Collapse — the query only runs when the operator
 * actually expands the row. Refetched when the parent bumps a
 * refresh key.
 */
export const BatchDeliveriesExpansion: FC<{
  organizationId: number;
  batchId: number;
  /**
   * The org's ACTIVE channels (voided excluded server-side). A delivery
   * whose `deliveryChannelId` is absent from this list points at a
   * voided channel — its Redeliver button renders disabled (the
   * server-side `redeliverDelivery` would reject it loudly anyway).
   */
  channels: DataExportDeliveryChannel[];
  /**
   * Parent-driven refetch trigger. The parent batch row bumps this on
   * row-level redelivery success (`redeliverBatch`), which inserts
   * delivery rows this expansion's own query would otherwise miss until
   * the operator collapses + re-expands. `0` means "no parent-driven
   * refresh yet" and is ignored.
   */
  refreshKey: number;
  onRedeliverSettled: () => void;
}> = ({ organizationId, batchId, channels, refreshKey, onRedeliverSettled }) => {
  const classes = useSharedStyles();
  const { data, loading, error, refetch } = useQuery<DataExportBatchDeliveriesData, DataExportBatchDeliveriesVars>(
    DATA_EXPORT_BATCH_DELIVERIES,
    {
      variables: { organizationId, batchId },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );

  React.useEffect(() => {
    if (refreshKey === 0) return;
    refetch().catch(() => undefined);
    // refetch identity is stable enough for our purposes; intentionally
    // omitted to mirror the same pattern used by BatchHistoryTable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const activeChannelIds = useMemo(() => new Set(channels.map(c => c.id)), [channels]);
  const deliveries = data?.dataExportBatchDeliveries ?? [];

  if (error && deliveries.length === 0) {
    return (
      <Box padding={2}>
        <Typography variant="body2" color="error">
          Could not load deliveries: {error.message}
        </Typography>
      </Box>
    );
  }
  if (loading && deliveries.length === 0) {
    return (
      <Box padding={2}>
        <Typography variant="body2" color="textSecondary">
          Loading deliveries…
        </Typography>
      </Box>
    );
  }
  return (
    <Box className={classes.deliverySubTable} padding={1}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Kind</TableCell>
            <TableCell>Channel snapshot</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Completed</TableCell>
            <TableCell>Delivered location</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {deliveries.map(d => (
            <DeliveryRow
              key={d.id}
              delivery={d}
              organizationId={organizationId}
              channelActive={activeChannelIds.has(d.deliveryChannelId)}
              onRedelivered={() => {
                refetch().catch(() => undefined);
                onRedeliverSettled();
              }}
            />
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const DeliveryRow: FC<{
  delivery: DataExportDelivery;
  organizationId: number;
  /** False when the delivery's channel has since been voided. */
  channelActive: boolean;
  onRedelivered: () => void;
}> = ({ delivery, organizationId, channelActive, onRedelivered }) => {
  const classes = useSharedStyles();
  const fmt = useTimestampFormatter();
  const { loading, invoke } = useInvoker<RedeliverDeliveryData, RedeliverDeliveryVars>({
    mutation: REDELIVER_DELIVERY,
    extractResult: d => redeliverOutcome(d?.redeliverDelivery),
    label: 'Redeliver attempt',
    onSettled: onRedelivered,
  });

  // Mirrors the server-side gate: `redeliverDelivery` rejects loudly
  // when the source row is not terminal (success/failed) or its channel
  // was voided. Greying the button keeps the operator from firing a
  // mutation the server is guaranteed to reject.
  const terminal = delivery.status === 'success' || delivery.status === 'failed';
  const canRedeliver = terminal && channelActive;
  const tooltip = canRedeliver
    ? 'Retry this attempt (creates a new delivery row)'
    : !channelActive
      ? 'Channel has been voided — cannot redeliver to it'
      : 'Delivery is still in flight';

  return (
    <TableRow>
      <TableCell className={classes.cellNowrap}>{delivery.kind}</TableCell>
      <TableCell className={classes.configMono}>{formatChannelSnapshot(delivery.channelSnapshotJson)}</TableCell>
      <TableCell>
        <DeliveryStatusChip status={delivery.status} />
        {delivery.errorMessage && (
          <Tooltip title={delivery.errorMessage}>
            <Typography variant="caption" color="error" className={classes.deliveryErrorLabel}>
              error
            </Typography>
          </Tooltip>
        )}
      </TableCell>
      <TableCell className={classes.cellTimestamp}>{fmt(delivery.createdAt)}</TableCell>
      <TableCell className={classes.cellTimestamp}>{fmt(delivery.completedAt)}</TableCell>
      <TableCell className={classes.configMono}>{delivery.deliveredLocation ?? '—'}</TableCell>
      <TableCell align="center">
        <Tooltip title={tooltip}>
          <span>
            <IconButton
              size="small"
              onClick={() => invoke({ organizationId, deliveryId: delivery.id }, false)}
              disabled={!canRedeliver || loading}
              aria-label="redeliver-delivery"
            >
              {loading ? <CircularProgress size={14} /> : <ReplayIcon fontSize="inherit" />}
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};
